import { FormStateType } from "./Helpers/formTypes";
import { guid } from "./Helpers/guid";
import { LocationDto } from "../../../Api/Model/LocationDto.csharp";
import { formatISO9075 } from "date-fns";
import { DateFormatter } from "../../../AdminPortal/Features/DateFormatter";

declare var dataLayer: any[] | undefined;

export class GtmService {
  static orderConfirmed(
    formState: FormStateType,
    location: LocationDto | undefined
  ) {
    if (typeof dataLayer != "undefined") {
      const ecommerce = {
        ecommerce: null,
      };

      const data = {
        ecommerce: {
          purchase: {
            actionField: {
              id: guid(),
              affiliation: location?.name ?? formState.location.name,
              revenue: formState.pricingSummary.basePrice,
            },
            products: [
              {
                name: formState.service.name,
                id: "",
                price: formState.pricingSummary.basePrice,
                category: location?.name ?? formState.location.name,
                variant: formState.specializationName,
                quantity: 1,
              },
            ],
          },
        },
      };
      const event = {
        event: "purchase",
      };

      const category = {
        category: location?.name ?? formState.location.name,
        id: "",
        name: formState.service.name,
        price: formState.pricingSummary.basePrice,
        variant: formState.specializationName,
      };

      const specializationSelected = {
        event: "specializationSelected",
        specializationName: formState.specializationName,
      };

      dataLayer.push(ecommerce);
      dataLayer.push(data);
      dataLayer.push(event);
      dataLayer.push(category);
      dataLayer.push(specializationSelected);
    }
  }

  static dropInConfirmed(serviceName: string, centerName: string, id: string) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "WaitingListConfirmed",
        product: serviceName,
        center: centerName,
        uniqueID: id,
      });
    }
  }

  static bookingCtaClicked(
    buttonVariant?: string,
    buttonLabel?: string,
    buttonUrl?: string
  ) {
    if (typeof dataLayer != "undefined" && !!buttonVariant) {
      dataLayer.push({
        event: "booking_cta_clicked",
        event_name: "Booking CTA clicked",
        info: {
          button_position: buttonVariant,
          button_label: buttonLabel,
          button_type: buttonUrl ? "link" : "open_booking",
          button_url: buttonUrl,
        },
      });
    }
  }

  static smsRequested() {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "SMSsent",
      });
    }
  }

  static confirmationError(
    service: string,
    specialization: string,
    center: string,
    message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "booking_confirmation_error",
        stage: "step_3",
        info: {
          name: service,
          category: center,
          variant: specialization,
          message: message,
        },
      });
    }
  }

  static bookingCreationError(
    service: string,
    specialization: string,
    center: string,
    message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "booking_creation_error",
        stage: "step_3",
        info: {
          name: service,
          category: center,
          variant: specialization,
          message: message,
        },
      });
    }
  }

  static appointmentsLoadingError(
    service: string,
    specialization: string,
    center: string,
    message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "appointments_loading_error",
        stage: "step_2",
        info: {
          name: service,
          category: center,
          variant: specialization,
          message: message,
        },
      });
    }
  }

  static genericError(message: string) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "unknown_error",
        stage: "unknown",
        info: {
          message: message,
        },
      });
    }
  }

  static bookingNoOnlineBooking(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    message: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_error",
        error_type: "No Online Booking",
        stage: "0",
        info: {
          service: specializationName || null,
          service_id: specializationId || null,
          treatment: serviceName || null,
          treatment_id: serviceId || null,
          center: centerName || null,
          center_id: centerId || null,
          message: message || null,
        },
      });
    }
  }

  static appointmentDateSelected(appointmentDate: Date) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "appointment_date_selected",
        event_name: "Appointment date selected",
        date: DateFormatter.formatDate(appointmentDate),
      });
    }
  }

  static noAppointmentsToday() {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "no_appointments_today",
        event_name: "No appointments today",
      });
    }
  }

  static firstAvailableDateSelected() {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "next_appointment_selected",
        event_name: "Next appointment selected",
      });
    }
  }

  static bookingDroppedOff() {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_dropped_off",
        event_name: "Booking dropped off",
      });
    }
  }

  static trackExperiment(experimentName: string, isExperimentEnabled: boolean) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "experiment_viewed",
        event_name: "Experiment Viewed",
        experiment_name: experimentName,
        experiment_variant: isExperimentEnabled ? "B" : "A",
      });
    }
  }

  static bookingOverlayLoaded(
    containerId?: string,
    specializationName?: string,
    specializationId?: string,
    serviceName?: string,
    serviceId?: string,
    centerName?: string,
    centerId?: string,
    doctorName?: string,
    doctorId?: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Booking Overlay Opened",
        booking: {
          desc: "booking_overlay_opened",
          tracking_code: "a1",
          info: {
            step: 0,
            current_booking_container_id: containerId || null,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
          },
        },
      });
    }
  }

  static appointmentStepSubmit(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName: string,
    doctorId: string,
    appointmentDate: Date,
    breadcrumbsShown?: boolean
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Appointment Selected",
        booking: {
          desc: "booking_step_two",
          tracking_code: "a3",
          info: {
            step: 2,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            appointment_date: GtmService.formatAppointmentDate(appointmentDate),
            appointment_date_unix_time: appointmentDate.getTime(),
            breadcrumb_shown: breadcrumbsShown,
          },
        },
      });
    }
  }

  static personalInfoStepSubmit(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName: string,
    doctorId: string,
    appointmentDate: Date,
    newsletterSignIn: boolean,
    price: number,
    breadcrumbsShown?: boolean
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Contact Information Submitted",
        booking: {
          desc: "booking_step_three",
          tracking_code: "a4",
          info: {
            step: 3,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            appointment_date: GtmService.formatAppointmentDate(appointmentDate),
            appointment_date_unix_time: appointmentDate.getTime(),
            sms_reminder: true,
            newsletterSignIn: newsletterSignIn,
            price: price,
            breadcrumb_shown: breadcrumbsShown,
          },
        },
      });
    }
  }

  static appointmentConfirmed(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName: string,
    doctorId: string,
    appointmentDate: Date,
    newsletterSignIn: boolean,
    price: number,
    breadcrumbsShown?: boolean
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Booking Confirmed",
        booking: {
          desc: "booking_confirmed",
          tracking_code: "a5",
          info: {
            step: 4,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            appointment_date: GtmService.formatAppointmentDate(appointmentDate),
            appointment_date_unix_time: appointmentDate.getTime(),
            sms_reminder: true,
            newsletter_sign_up: newsletterSignIn,
            price: price,
            appointment_id: guid(),
            breadcrumb_shown: breadcrumbsShown,
          },
        },
      });
    }
  }

  static bookingAppointmentsScreenShown(
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName?: string,
    doctorId?: string,
    isBookingFallback?: boolean,
    breadcrumbsShown?: boolean
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Appointments Screen Shown",
        booking: {
          desc: "appointments_shown",
          tracking_code: "a9",
          info: {
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
            booking_fallback: isBookingFallback || false,
            breadcrumb_shown: breadcrumbsShown,
          },
        },
      });
    }
  }

  static initialStepSubmit(
    currentContainerId: string,
    targetContainerId: string,
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName?: string,
    doctorId?: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Initial Step Submitted",
        booking: {
          desc: "initial_step_submitted",
          tracking_code: "a6",
          info: {
            step: 0,
            current_booking_container_id: currentContainerId || null,
            target_booking_container_id: targetContainerId || null,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
          },
        },
      });
    }
  }

  static treatmentStepSubmit(
    currentContainerId: string,
    targetContainerId: string,
    specializationName: string,
    specializationId: string,
    serviceName: string,
    serviceId: string,
    centerName: string,
    centerId: string,
    doctorName?: string,
    doctorId?: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        ab_test_variant: "new_booking_flow",
        event_name: "Treatment Step Submitted",
        booking: {
          desc: "treatment_step_submitted",
          tracking_code: "a7",
          info: {
            step: 0,
            current_booking_container_id: currentContainerId || null,
            target_booking_container_id: targetContainerId || null,
            service: specializationName || null,
            service_id: specializationId || null,
            treatment: serviceName || null,
            treatment_id: serviceId || null,
            center: centerName || null,
            center_id: centerId || null,
            treatment_provider: doctorName || null,
            doctor_id: doctorId || null,
          },
        },
      });
    }
  }

  static backButtonSelected(
    currentContainerId: string,
    targetContainerId: string
  ) {
    if (typeof dataLayer != "undefined") {
      dataLayer.push({
        event: "booking_flow",
        event_name: "Back Button Selected",
        booking: {
          desc: "back_button_selected",
          tracking_code: "a10",
          info: {
            step: 0,
            current_booking_container_id: currentContainerId || null,
            target_booking_container_id: targetContainerId || null,
          },
        },
      });
    }
  }

  private static formatAppointmentDate(appointmentDate: Date) {
    return formatISO9075(appointmentDate);
  }
}
